import React, { useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import LimitReachedModal from "../LimitReachedModal";
import { Loader2, Sparkles, Save, RefreshCw, Copy, Check, X } from "lucide-react";

const GENRES = [
  "Children's Educational",
  "Self-Help & Personal Development",
  "Business & Entrepreneurship",
  "Health & Wellness",
  "Fiction – Thriller",
  "Fiction – Romance",
  "Fiction – Historical",
  "Cookbooks & Food",
  "Biographies & Memoirs",
  "Other",
];

const FORMATS = ["Ebook", "Paperback", "Hardcover", "All formats"];

const initialForm = {
  book_title: "",
  subtitle: "",
  genre: GENRES[0],
  book_format: "Ebook",
  page_count: "",
  summary: "",
  target_reader: "",
  similar_books: "",
};

const ListingGenerator = () => {
  const { profile, refreshProfile } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");
  const [savedId, setSavedId] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleGenerate = async () => {
    setError("");
    setLoading(true);
    setSavedId(null);
    try {
      const payload = {
        ...form,
        page_count: form.page_count ? Number(form.page_count) : null,
      };
      const { data } = await api.post("/listing/generate", payload);
      setResult(data);
      setKeywords(data.keywords || []);
      refreshProfile();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "limit_reached") {
        setLimitInfo({ type: detail.type || "listing", plan: detail.plan || profile?.plan || "free" });
      } else {
        setError(typeof detail === "string" ? detail : "Generation failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const removeKeyword = (kw) => setKeywords((k) => k.filter((x) => x !== kw));

  const applyHeadline = (h) => {
    if (!result) return;
    // Replace first <p>...</p> or first sentence
    const desc = result.description;
    const replaced = desc.replace(/^(<b>)?[^<]*?(<\/b>)?(?=<br|<p|$)/, `<b>${h}</b>`);
    setResult({ ...result, description: replaced });
  };

  const saveListing = async () => {
    if (!result) return;
    try {
      const { data } = await api.post("/listing/save", {
        book_title: form.book_title,
        genre: form.genre,
        description_html: result.description,
        keywords,
        categories: result.categories,
        alternative_headlines: result.alternative_headlines,
        raw_input: form,
      });
      setSavedId(data.id || "saved");
    } catch (e) {
      setError("Save failed");
    }
  };

  const isFormValid = form.book_title && form.summary && form.genre;

  return (
    <div className="grid lg:grid-cols-12 gap-8 mt-8">
      {/* FORM */}
      <section className="lg:col-span-5">
        <div className="bg-white border border-[#D3D1C7] rounded-[12px] p-6 sticky top-[88px]">
          <div className="flex items-center gap-2 mb-5">
            <Sparkles className="w-4 h-4 text-[#534AB7]" strokeWidth={1.5} />
            <h2 className="text-lg font-medium" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
              Generate a listing
            </h2>
          </div>

          <div className="space-y-4">
            <Field label="Book title" required>
              <input
                value={form.book_title}
                onChange={(e) => update("book_title", e.target.value)}
                data-testid="input-book-title"
                className={inputCls}
                placeholder="Maths Monsters: Year 2 Workbook"
              />
            </Field>
            <Field label="Subtitle (optional)">
              <input
                value={form.subtitle}
                onChange={(e) => update("subtitle", e.target.value)}
                className={inputCls}
                placeholder="Fun activity puzzles for ages 6–7"
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Genre" required>
                <select
                  value={form.genre}
                  onChange={(e) => update("genre", e.target.value)}
                  data-testid="select-genre"
                  className={inputCls}
                >
                  {GENRES.map((g) => (
                    <option key={g}>{g}</option>
                  ))}
                </select>
              </Field>
              <Field label="Format">
                <select
                  value={form.book_format}
                  onChange={(e) => update("book_format", e.target.value)}
                  className={inputCls}
                >
                  {FORMATS.map((f) => (
                    <option key={f}>{f}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Page count (optional)">
              <input
                type="number"
                value={form.page_count}
                onChange={(e) => update("page_count", e.target.value)}
                className={inputCls}
                placeholder="120"
              />
            </Field>
            <Field label="What's the book about?" required>
              <textarea
                value={form.summary}
                onChange={(e) => update("summary", e.target.value)}
                rows={4}
                data-testid="input-summary"
                className={`${inputCls} resize-none`}
                placeholder="2–5 sentences describing the book, its premise, and what makes it different."
              />
            </Field>
            <Field label="Target reader (optional)">
              <input
                value={form.target_reader}
                onChange={(e) => update("target_reader", e.target.value)}
                className={inputCls}
                placeholder="Parents of 6–7 year-olds doing KS1 maths"
              />
            </Field>
            <Field label="Similar books (optional)">
              <input
                value={form.similar_books}
                onChange={(e) => update("similar_books", e.target.value)}
                className={inputCls}
                placeholder="Brain Quest Workbook, Carol Vorderman Maths"
              />
            </Field>
          </div>

          <button
            onClick={handleGenerate}
            disabled={!isFormValid || loading}
            data-testid="generate-listing-btn"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[8px] bg-[#534AB7] text-white font-medium hover:bg-[#3C3489] transition-colors disabled:opacity-50 shadow-sm"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? "Generating with Claude…" : "Generate listing"}
          </button>

          {error && <p className="mt-3 text-sm text-[#BA7517]" data-testid="listing-error">{error}</p>}
        </div>
      </section>

      {/* OUTPUT */}
      <section className="lg:col-span-7">
        {!result ? (
          <div className="bg-[#F1EFE8]/60 border border-dashed border-[#D3D1C7] rounded-[12px] p-12 text-center">
            <Sparkles className="w-7 h-7 text-[#534AB7] mx-auto" strokeWidth={1.5} />
            <h3 className="mt-4 text-xl tracking-tight" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}>
              Your listing will appear here
            </h3>
            <p className="mt-2 text-sm text-[#5F5E5A] max-w-md mx-auto">
              Fill out the form on the left and Claude will generate a publish-ready Amazon listing in
              about 10 seconds.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Description */}
            <div className="bg-white border border-[#D3D1C7] rounded-[12px] overflow-hidden" data-testid="listing-output-description">
              <div className="px-5 py-3 border-b border-[#D3D1C7] bg-[#FAFAF9] flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A]">Description (Amazon HTML)</span>
                <CopyBtn k="desc" copied={copied} onClick={() => copy(result.description, "desc")} label="Copy HTML" testid="copy-description-btn" />
              </div>
              <div
                className="p-5 text-sm leading-relaxed max-h-[420px] overflow-y-auto text-[#2C2C2A] prose prose-sm"
                dangerouslySetInnerHTML={{ __html: result.description }}
              />
            </div>

            {/* Keywords */}
            <div className="bg-white border border-[#D3D1C7] rounded-[12px] p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A]">Keywords ({keywords.length})</span>
                <CopyBtn k="kw" copied={copied} onClick={() => copy(keywords.join(", "), "kw")} label="Copy all" testid="copy-keywords-btn" />
              </div>
              <div className="flex flex-wrap gap-2" data-testid="keyword-pills">
                {keywords.map((kw) => (
                  <span key={kw} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-[#F1EFE8] border border-[#D3D1C7] text-sm">
                    {kw}
                    <button
                      onClick={() => removeKeyword(kw)}
                      className="w-5 h-5 rounded-full hover:bg-white text-[#5F5E5A] flex items-center justify-center"
                      aria-label={`Remove ${kw}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div className="bg-white border border-[#D3D1C7] rounded-[12px] p-5">
              <span className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A]">Browse Categories</span>
              <ul className="mt-3 space-y-2" data-testid="categories-list">
                {result.categories.map((c, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-[#2C2C2A] flex-1">{c}</span>
                    <CopyBtn k={`c${i}`} copied={copied} onClick={() => copy(c, `c${i}`)} label="Copy" />
                  </li>
                ))}
              </ul>
            </div>

            {/* Alternative headlines */}
            <div className="bg-white border border-[#D3D1C7] rounded-[12px] p-5">
              <span className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A]">Alternative opening hooks</span>
              <ul className="mt-3 space-y-3" data-testid="alt-headlines-list">
                {result.alternative_headlines.map((h, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 bg-[#FAFAF9] rounded-[8px] p-3 border border-[#D3D1C7]">
                    <span className="text-sm text-[#2C2C2A] flex-1 italic">"{h}"</span>
                    <button
                      onClick={() => applyHeadline(h)}
                      className="px-3 py-1 rounded-md text-xs border border-[#534AB7] text-[#534AB7] hover:bg-[#534AB7] hover:text-white transition-colors"
                      data-testid={`use-headline-${i}`}
                    >
                      Use this
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={saveListing}
                disabled={!!savedId}
                data-testid="save-listing-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] transition-colors disabled:opacity-50"
              >
                {savedId ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                {savedId ? "Saved to library" : "Save to library"}
              </button>
              <button
                onClick={handleGenerate}
                disabled={loading}
                data-testid="regenerate-listing-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[8px] border border-[#D3D1C7] text-[#2C2C2A] text-sm font-medium hover:bg-[#F1EFE8] transition-colors"
              >
                <RefreshCw className="w-4 h-4" /> Regenerate
              </button>
            </div>
          </div>
        )}
      </section>
      <LimitReachedModal
        open={!!limitInfo}
        type={limitInfo?.type || "listing"}
        plan={limitInfo?.plan || "free"}
        onClose={() => setLimitInfo(null)}
      />
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-[#5F5E5A] uppercase tracking-wider mb-1.5">
      {label} {required && <span className="text-[#534AB7]">*</span>}
    </span>
    {children}
  </label>
);

const inputCls = "w-full px-3 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-sm text-[#2C2C2A] focus:outline-none focus:border-[#534AB7] focus:ring-1 focus:ring-[#534AB7] placeholder:text-[#5F5E5A]/50";

const CopyBtn = ({ k, copied, onClick, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border border-[#D3D1C7] text-[#2C2C2A] hover:bg-[#F1EFE8] transition-colors"
  >
    {copied === k ? <Check className="w-3 h-3 text-[#1D9E75]" /> : <Copy className="w-3 h-3" />}
    {label || (copied === k ? "Copied" : "Copy")}
  </button>
);

export default ListingGenerator;
